import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollSentinelOptions {
    enabled: boolean;
    onTrigger: () => void;
    /**
     * Root margin in pixels around the viewport. Defaults to 2000px to match
     * the threshold the skeleton lazy-load uses elsewhere in the grid.
     * IntersectionObserver doesn't accept `vh` units.
     */
    rootMarginPx?: number;
}

/**
 * Returns a ref callback to attach to a sentinel element placed at the end
 * of a virtualized list. Fires `onTrigger` when the sentinel scrolls into
 * the expanded viewport (default: 2000px before the bottom edge).
 *
 * Continuous-load semantics: the observer fires `onTrigger` on a fresh
 * `isIntersecting=true` callback (the user scrolling the sentinel into the
 * zone). While a load runs `enabled` is false so those callbacks are ignored;
 * a page rarely adds 2000px of height, so once the load completes the sentinel
 * is usually still inside the zone with no further transition to fire on. To
 * keep paging in that case, re-fire once when `enabled` flips back to true and
 * the sentinel is still intersecting — paging then continues until the
 * sentinel finally leaves the zone or `hasMore` goes false. The observer is
 * never re-created here, so this does not reintroduce the double-fire flash
 * that re-observing on every `enabled` flip used to cause.
 */
export const useInfiniteScrollSentinel = ({
    enabled,
    onTrigger,
    rootMarginPx,
}: UseInfiniteScrollSentinelOptions) => {
    const onTriggerRef = useRef(onTrigger);
    onTriggerRef.current = onTrigger;

    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    const isIntersectingRef = useRef(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const nodeRef = useRef<HTMLElement | null>(null);

    const setRef = useCallback(
        (node: HTMLElement | null) => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            nodeRef.current = node;
            isIntersectingRef.current = false;
            if (!node || typeof IntersectionObserver === 'undefined') return;

            const margin = rootMarginPx ?? 2000;

            const observer = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        isIntersectingRef.current = entry.isIntersecting;
                        if (entry.isIntersecting && enabledRef.current) {
                            onTriggerRef.current();
                        }
                    }
                },
                // The zone also reaches one viewport above: a reader who
                // outruns a slow page lands just past the sentinel — scroll
                // anchoring holds them there as the tiles insert above — and
                // a bottom-only zone could never fire again from there. One
                // viewport, not the full margin: a reader parked in a long
                // tail (recently viewed, the footer) is reading, not waiting,
                // and a deeper reach would page the whole catalogue under
                // them.
                { rootMargin: `100% 0px ${margin}px 0px`, threshold: 0 },
            );
            observer.observe(node);
            observerRef.current = observer;
        },
        [rootMarginPx],
    );

    // A load finishing flips `enabled` false -> true. If the sentinel never
    // left the zone during the fetch the observer won't emit a new callback,
    // so fire once here to load the next page. No-ops on mount (the sentinel
    // is not yet intersecting) and whenever `enabled` is false.
    useEffect(() => {
        if (enabled && isIntersectingRef.current) {
            onTriggerRef.current();
        }
    }, [enabled]);

    useEffect(() => {
        return () => {
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
    }, []);

    return setRef;
};
